import React, { useCallback, useEffect, useMemo, useState } from 'react';
import opensearchService from '../services/opensearchService';
import partnerService from '../services/partnerService';
import accountService from '../services/accountService';
import S3FilePickerModal from './S3FilePickerModal';
import './OpenSearchAdmin.css';

// Payment Token Swap — pick a partner + chain to browse that pairing's payment-token index
// (`<env>-<region>-<partnerId>-<chainId>-payment-token`, columns id_from_source / id_for_target),
// and create/replace it from a file in S3. Admin screen — ignores the navbar chain context.

const SUFFIX = '-payment-token';
// ENV_REGION (e.g. "dev-euw1") derived from the vault-upload bucket env var (dev-euw1-vault-upload).
const ENV_REGION = (process.env.REACT_APP_VAULT_UPLOAD_BUCKET || '').replace(/-vault-upload$/, '');
const PAGE_SIZE = 50;
const fmt = (n) => (n != null ? Number(n).toLocaleString() : '-');

const partnerIdOf = (p) => p.partnerid ?? p.id;
const partnerNameOf = (p) => p.companyname ?? p.name ?? `Partner ${partnerIdOf(p)}`;
const chainIdOf = (a) => a.accountid ?? a.ACCOUNTID ?? a.id;
const chainNameOf = (a) => a.companyname ?? a.COMPANYNAME ?? a.name ?? `Chain ${chainIdOf(a)}`;

const isNotFound = (e) =>
    e.response?.status === 404 ||
    /index_not_found|no such index|not.?found/i.test(e.response?.data?.error?.message || e.response?.data?.message || e.message || '');

// Parse "bucket/folder/…" (optionally s3://…) into { bucket, prefix } for an S3 picker.
const parseS3Loc = (loc) => {
    const s = String(loc || '').replace(/^s3:\/\//, '').replace(/^\/+/, '');
    const slash = s.indexOf('/');
    const bucket = (slash === -1 ? s : s.slice(0, slash)) || null;
    let prefix = slash === -1 ? '' : s.slice(slash + 1);
    if (prefix && !prefix.endsWith('/')) prefix += '/';
    return { bucket, prefix };
};

export default function PaymentTokenSwap() {
    const [partners, setPartners] = useState([]);
    const [chains, setChains] = useState([]);
    const [partnerId, setPartnerId] = useState('');
    const [chainId, setChainId] = useState('');
    // Secrets Manager secret holding the PGP encryption key pair; suggested per-environment default.
    const [secretName, setSecretName] = useState(ENV_REGION ? `${ENV_REGION}-encryption-keypair` : '');
    // S3 location (bucket[/prefix]) the Decrypt picker opens into by default (prefilled, editable).
    // Dev drops files under .../staging; prod under .../prd.
    const [decryptLocation, setDecryptLocation] = useState(
        ENV_REGION ? `${ENV_REGION}-external-shared/${ENV_REGION.startsWith('prd') ? 'prd' : 'staging'}` : ''
    );

    const [docs, setDocs] = useState([]);
    const [total, setTotal] = useState(0);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [notFound, setNotFound] = useState(false);
    const [from, setFrom] = useState(0);
    const [inputQ, setInputQ] = useState('');
    const [q, setQ] = useState('');
    const [expanded, setExpanded] = useState(null);

    const [pickerOpen, setPickerOpen] = useState(false);
    const [selectedFile, setSelectedFile] = useState(null);
    const [creating, setCreating] = useState(false);
    const [createMsg, setCreateMsg] = useState(null);
    const [createErr, setCreateErr] = useState(null);
    const [purging, setPurging] = useState(false);

    // Decrypt section
    const [decryptPickerOpen, setDecryptPickerOpen] = useState(false);
    const [decryptFiles, setDecryptFiles] = useState([]);
    const [decrypting, setDecrypting] = useState(false);
    const [decryptMsg, setDecryptMsg] = useState(null);
    const [decryptErr, setDecryptErr] = useState(null);

    const [activeTab, setActiveTab] = useState('tokens');
    const [histDocs, setHistDocs] = useState([]);
    const [histTotal, setHistTotal] = useState(0);
    const [histLoading, setHistLoading] = useState(false);
    const [histError, setHistError] = useState(null);
    const [histFrom, setHistFrom] = useState(0);

    // The index chosen by the partner + chain selection.
    const indexName = partnerId && chainId ? `${ENV_REGION}-${partnerId}-${chainId}${SUFFIX}` : '';

    // Load partners once; load a partner's chains when it changes.
    useEffect(() => {
        partnerService.getAllPartners().then(r => setPartners(r.data?.partners || [])).catch(() => {});
    }, []);
    useEffect(() => {
        setChainId('');
        if (!partnerId) { setChains([]); return; }
        accountService.getAllAccounts({ partnerid: partnerId }).then(d => setChains(d.accounts || [])).catch(() => setChains([]));
    }, [partnerId]);

    const load = useCallback(async () => {
        if (!indexName) { setDocs([]); setTotal(0); setNotFound(false); return; }
        setLoading(true); setError(null); setNotFound(false);
        try {
            // Name the fields to search. The default free-text path queries `message`, which these
            // documents do not have — so the box matched nothing whatever was typed, including a token
            // that was demonstrably in the index.
            const res = await opensearchService.searchDocs(indexName, {
                q, qFields: 'id_from_source,id_for_target', size: PAGE_SIZE, from,
            });
            setDocs(res.data?.hits || []);
            setTotal(res.data?.total || 0);
        } catch (e) {
            if (isNotFound(e)) { setNotFound(true); setDocs([]); setTotal(0); }
            else { setError(e.response?.data?.message || e.message || 'Failed to load payment tokens'); setDocs([]); setTotal(0); }
        } finally {
            setLoading(false);
        }
    }, [indexName, q, from]);

    useEffect(() => { load(); }, [load]);
    // Reset paging/search when the chosen index changes.
    useEffect(() => { setFrom(0); setInputQ(''); setQ(''); setExpanded(null); setHistFrom(0); }, [indexName]);
    // Debounce the free-text search.
    useEffect(() => {
        const t = setTimeout(() => { setQ(inputQ.trim()); setFrom(0); }, 350);
        return () => clearTimeout(t);
    }, [inputQ]);

    // Upsert history (sibling <index>-history index), newest first.
    const loadHistory = useCallback(async () => {
        if (!indexName) { setHistDocs([]); setHistTotal(0); return; }
        setHistLoading(true); setHistError(null);
        try {
            const res = await opensearchService.searchDocs(`${indexName}-history`, { size: PAGE_SIZE, from: histFrom });
            const hits = (res.data?.hits || []).slice().sort((a, b) =>
                String(b.source?.created_at || '').localeCompare(String(a.source?.created_at || '')));
            setHistDocs(hits);
            setHistTotal(res.data?.total || 0);
        } catch (e) {
            if (isNotFound(e)) { setHistDocs([]); setHistTotal(0); }
            else setHistError(e.response?.data?.message || e.message || 'Failed to load history');
        } finally {
            setHistLoading(false);
        }
    }, [indexName, histFrom]);
    useEffect(() => { if (activeTab === 'history') loadHistory(); }, [activeTab, loadHistory]);

    // Set of S3 keys already successfully upserted into THIS index (from <index>-history), so the
    // "Upsert from S3" picker can mark each decrypted file green (in index) or amber (needs upsert).
    const [uploadedKeys, setUploadedKeys] = useState(() => new Set());
    const loadUploadedKeys = useCallback(async () => {
        if (!indexName) { setUploadedKeys(new Set()); return; }
        try {
            const res = await opensearchService.searchDocs(`${indexName}-history`, { size: 1000, from: 0 });
            const keys = new Set();
            for (const h of (res.data?.hits || [])) {
                const s = h.source || {};
                if (s.key && (s.status ? s.status === 'success' : true)) keys.add(s.key);
            }
            setUploadedKeys(keys);
        } catch (_) { setUploadedKeys(new Set()); }
    }, [indexName]);
    useEffect(() => { loadUploadedKeys(); }, [loadUploadedKeys]);

    const handleDecrypt = async () => {
        if (!decryptFiles.length || !secretName) return;
        const total = decryptFiles.length;
        setDecrypting(true); setDecryptErr(null); setDecryptMsg(`Starting decrypt of ${total} file(s)…`);
        // Kick off one decrypt job per selected file, then poll them all to completion.
        const jobs = [];
        for (const f of decryptFiles) {
            try {
                const res = await opensearchService.startPaymentTokenDecrypt({ bucket: f.bucket, key: f.key, secretName });
                jobs.push({ file: f, jobId: res.data?.jobId, done: false, ok: null, error: null });
            } catch (e) {
                jobs.push({ file: f, jobId: null, done: true, ok: false, error: e.response?.data?.error?.message || e.message });
            }
        }
        const fileLabel = (f) => f.name || (f.key || '').split('/').pop() || f.key;
        const started = Date.now();
        const poll = async () => {
            for (const j of jobs) {
                if (j.done || !j.jobId) continue;
                try {
                    const job = (await opensearchService.getPaymentTokenDecryptStatus(j.jobId)).data?.job;
                    if (job?.status === 'success') { j.done = true; j.ok = true; }
                    else if (job?.status === 'error') { j.done = true; j.ok = false; j.error = job.error || 'Decrypt failed'; }
                } catch (_) { /* keep polling */ }
            }
            const done = jobs.filter(j => j.done).length;
            const failedJobs = jobs.filter(j => j.done && j.ok === false);
            if (done >= total) {
                setDecrypting(false);
                setDecryptMsg(`Decrypted ${done - failedJobs.length}/${total}${failedJobs.length ? ` — ${failedJobs.length} failed` : ''}. Output → decrypted/; sources archived → encrypted-archived/.`);
                // Surface the actual per-file error(s) (e.g. the key-id mismatch), not just a count.
                setDecryptErr(failedJobs.length
                    ? failedJobs.map(j => `${fileLabel(j.file)}: ${j.error || 'decrypt failed'}`).join('\n')
                    : null);
                setDecryptFiles([]);
                return;
            }
            setDecryptMsg(`Decrypting… ${done}/${total} done${failedJobs.length ? ` (${failedJobs.length} failed)` : ''}`);
            if (Date.now() - started > 15 * 60 * 1000) { setDecrypting(false); setDecryptErr('Timed out waiting for decrypt'); return; }
            setTimeout(poll, 4000);
        };
        setTimeout(poll, 4000);
    };

    const handleCreate = async () => {
        if (!selectedFile || !indexName) return;
        setCreating(true); setCreateErr(null); setCreateMsg(null);
        try {
            const res = await opensearchService.createPaymentTokenIndex({
                bucket: selectedFile.bucket, key: selectedFile.key, indexName,
            });
            // The import now runs asynchronously (202 accepted): the lambda processes the file in the
            // background under its full timeout, and the outcome lands in the token list + History.
            if (res.status === 202 || res.data?.status === 'accepted') {
                setCreateMsg(res.data?.message || 'Import started. It runs in the background — refresh History to see the result.');
            } else {
                // Back-compat with a synchronous response that carries counts.
                setCreateMsg(`${fmt(res.data.inserted)} inserted, ${fmt(res.data.updated)} updated (${fmt(res.data.total)} in file) → ${res.data.index}.`);
            }
            setSelectedFile(null);
            await load();
            // The job may still be running — nudge the views a few times so growing counts, the new
            // history entry, and the picker's in-index markers appear without a manual refresh.
            [5000, 15000, 30000].forEach((ms) => setTimeout(() => { load(); loadUploadedKeys(); if (activeTab === 'history') loadHistory(); }, ms));
        } catch (e) {
            setCreateErr(e.response?.data?.error?.message || e.response?.data?.message || e.message || 'Failed to start import');
        } finally {
            setCreating(false);
        }
    };

    const handlePurge = async () => {
        if (!indexName) return;
        if (!window.confirm(`Purge index "${indexName}"? This permanently deletes all of its payment tokens.`)) return;
        setPurging(true); setError(null); setCreateMsg(null);
        try {
            await opensearchService.deleteIndex(indexName);
            setCreateMsg(`Purged ${indexName}.`);
            await load();
        } catch (e) {
            setError(e.response?.data?.error?.message || e.response?.data?.message || e.message || 'Failed to purge index');
        } finally {
            setPurging(false);
        }
    };

    // Metadata fields hidden from the collapsed columns — they still show in the expanded JSON row.
    const HIDDEN_COLS = ['id', 'updated_at', 'log', 'created_at'];
    const cols = useMemo(() => {
        const seen = [];
        docs.forEach((d) => Object.keys(d.source || {}).forEach((k) => { if (!seen.includes(k) && !HIDDEN_COLS.includes(k)) seen.push(k); }));
        return seen.slice(0, 8);
    }, [docs]);

    const pages = Math.ceil(total / PAGE_SIZE);
    const page = Math.floor(from / PAGE_SIZE);

    return (
        <div className="acp-page" style={{ maxWidth: 1400 }}>
            <div className="acp-header">
                <div>
                    <h1 className="acp-title">Payment Token Swap</h1>
                    <p className="acp-subtitle">
                        {indexName ? `${fmt(total)} tokens · ${indexName}` : 'Select a partner and chain'}
                    </p>
                </div>
            </div>

            {/* Decrypt section — PGP-decrypt an S3 file with the keypair secret, writing a "-decrypted" copy. */}
            <div className="acp-card">
                <div className="acp-card-header"><span className="acp-card-title">Decrypt file</span></div>
                <div style={{ padding: '1rem', display: 'flex', flexDirection: 'column', gap: 12 }}>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 4, maxWidth: 480 }}>
                        <label style={{ fontSize: '0.8rem', color: '#6b7280', fontWeight: 600 }}>Secrets Manager secret name</label>
                        <input
                            type="text"
                            className="acp-input"
                            value={secretName}
                            onChange={e => setSecretName(e.target.value)}
                            placeholder={ENV_REGION ? `${ENV_REGION}-encryption-keypair` : 'dev-euw1-encryption-keypair'}
                        />
                    </div>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 4, maxWidth: 480 }}>
                        <label style={{ fontSize: '0.8rem', color: '#6b7280', fontWeight: 600 }}>S3 location (bucket/folder)</label>
                        <input
                            type="text"
                            className="acp-input"
                            value={decryptLocation}
                            onChange={e => setDecryptLocation(e.target.value)}
                            placeholder={ENV_REGION ? `${ENV_REGION}-external-shared/${ENV_REGION.startsWith('prd') ? 'prd' : 'staging'}` : 'dev-euw1-external-shared/staging'}
                        />
                    </div>
                    {decryptFiles.length > 0 && (
                        <ul style={{ margin: 0, paddingLeft: 18, fontSize: '0.82rem', color: '#374151', maxHeight: 140, overflow: 'auto' }}>
                            {decryptFiles.map(f => <li key={f.key}><code>{f.key}</code></li>)}
                        </ul>
                    )}
                    <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                        {decryptFiles.length > 0 ? (
                            <span style={{ fontSize: '0.9rem', display: 'inline-flex', gap: 8, alignItems: 'center' }}>
                                {decryptFiles.length} file(s) selected
                                <button className="acp-btn acp-btn-secondary acp-btn-sm" onClick={() => setDecryptPickerOpen(true)}>Change</button>
                                <button className="acp-btn acp-btn-secondary acp-btn-sm" onClick={() => setDecryptFiles([])}>Clear</button>
                            </span>
                        ) : (
                            <button className="acp-btn acp-btn-secondary" onClick={() => setDecryptPickerOpen(true)}>Select S3 files…</button>
                        )}
                        <button className="acp-btn acp-btn-primary" onClick={handleDecrypt} disabled={!decryptFiles.length || !secretName || decrypting}>
                            {decrypting ? 'Decrypting…' : `Decrypt${decryptFiles.length ? ` ${decryptFiles.length}` : ''}`}
                        </button>
                    </div>
                    {decryptMsg && <div className="os-msg" style={{ color: '#166534' }}>{decryptMsg}</div>}
                    {decryptErr && <div className="os-msg os-msg-error" style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>{decryptErr}</div>}
                </div>
            </div>

            {/* Partner + chain selection — chooses the index to display. */}
            <div className="acp-card">
                <div style={{ padding: '1rem', display: 'flex', flexDirection: 'column', gap: 12 }}>
                    <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
                        <select className="acp-input" style={{ minWidth: 260 }} value={partnerId} onChange={e => setPartnerId(e.target.value)}>
                            <option value="">Select partner…</option>
                            {partners.map(p => <option key={partnerIdOf(p)} value={partnerIdOf(p)}>{partnerNameOf(p)} ({partnerIdOf(p)})</option>)}
                        </select>
                        <select className="acp-input" style={{ minWidth: 260 }} value={chainId} onChange={e => setChainId(e.target.value)} disabled={!partnerId}>
                            <option value="">{partnerId ? 'Select chain…' : 'Select a partner first'}</option>
                            {chains.map(a => <option key={chainIdOf(a)} value={chainIdOf(a)}>{chainNameOf(a)} ({chainIdOf(a)})</option>)}
                        </select>
                    </div>
                    {indexName && (
                        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                            <button className="acp-btn acp-btn-primary" onClick={() => { loadUploadedKeys(); setPickerOpen(true); }}>Upsert from S3</button>
                            <button className="acp-btn acp-btn-secondary" onClick={() => (activeTab === 'history' ? loadHistory() : load())} disabled={loading || histLoading}>{(activeTab === 'history' ? histLoading : loading) ? 'Refreshing…' : 'Refresh'}</button>
                            {!notFound && (
                                <button className="acp-btn acp-btn-danger" onClick={handlePurge} disabled={purging || loading}>{purging ? 'Purging…' : 'Purge Index'}</button>
                            )}
                        </div>
                    )}
                </div>
            </div>

            {createMsg && <div className="os-msg" style={{ color: '#166534' }}>{createMsg}</div>}

            <div style={{ display: 'flex', gap: 4, borderBottom: '1px solid #e5e7eb', margin: '4px 0 16px' }}>
                {[['tokens', 'Tokens'], ['history', 'History']].map(([t, label]) => (
                    <button
                        key={t}
                        onClick={() => setActiveTab(t)}
                        style={{
                            border: 'none', background: 'none', padding: '10px 16px', cursor: 'pointer',
                            fontSize: '0.9rem', fontWeight: 600, marginBottom: -1,
                            color: activeTab === t ? '#4f46e5' : '#6b7280',
                            borderBottom: activeTab === t ? '2px solid #4f46e5' : '2px solid transparent',
                        }}
                    >{label}</button>
                ))}
            </div>

            {activeTab === 'tokens' && (<>
            {error && <div className="os-msg os-msg-error">{error}</div>}

            {/* Create / replace from the selected S3 file. */}
            {selectedFile && (
                <div className="acp-card">
                    <div className="acp-card-header"><span className="acp-card-title">Add payment tokens from file</span></div>
                    <div style={{ padding: '1rem', display: 'flex', flexDirection: 'column', gap: 12 }}>
                        <div style={{ fontSize: '0.9rem' }}>
                            File: <code>s3://{selectedFile.bucket}/{selectedFile.key}</code>{' '}
                            <button className="acp-btn acp-btn-secondary acp-btn-sm" onClick={() => setSelectedFile(null)}>Change</button>
                        </div>
                        <div style={{ fontSize: '0.85rem', color: '#6b7280' }}>Target index: <code>{indexName}</code></div>
                        {createErr && <div className="os-msg os-msg-error">{createErr}</div>}
                        <div>
                            <button className="acp-btn acp-btn-primary" onClick={handleCreate} disabled={creating || !indexName}>
                                {creating ? 'Adding…' : 'Add to index'}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Documents */}
            {indexName && (
                <div className="acp-card">
                    <div className="os-search-bar">
                        <input
                            className="acp-input os-search-input"
                            placeholder="Search all fields…"
                            value={inputQ}
                            onChange={(e) => setInputQ(e.target.value)}
                            disabled={notFound}
                        />
                        {loading && <span className="os-loading-dot">Searching…</span>}
                        {!notFound && (
                            <span style={{ marginLeft: 'auto', color: '#6b7280', fontSize: '0.82rem', whiteSpace: 'nowrap' }}>
                                {q ? `${fmt(total)} match${total === 1 ? '' : 'es'}` : `${fmt(total)} token${total === 1 ? '' : 's'} in index`}
                            </span>
                        )}
                    </div>

                    {notFound ? (
                        <div className="acp-empty">No payment-token index for this partner / chain yet. Use <strong>+ Add from S3</strong> to create one.</div>
                    ) : docs.length === 0 && !loading ? (
                        <div className="acp-empty">{q ? 'No tokens match your search' : 'No payment tokens in this index'}</div>
                    ) : (
                        <>
                            <div className="os-doc-table-wrap">
                                <table className="acp-table os-doc-table">
                                    <thead>
                                        <tr>
                                            <th>ID</th>
                                            {cols.map((c) => <th key={c}>{c}</th>)}
                                            <th></th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {docs.map((d) => (
                                            <React.Fragment key={d.id}>
                                                <tr
                                                    className={expanded === d.id ? 'os-doc-row-expanded' : ''}
                                                    onClick={() => setExpanded(expanded === d.id ? null : d.id)}
                                                    style={{ cursor: 'pointer' }}
                                                >
                                                    <td className="os-doc-id" title={d.id}>{d.id?.substring(0, 8)}…</td>
                                                    {cols.map((c) => <td key={c}>{d.source?.[c] != null ? String(d.source[c]) : ''}</td>)}
                                                    <td><span className="os-expand-icon">{expanded === d.id ? '▲' : '▼'}</span></td>
                                                </tr>
                                                {expanded === d.id && (
                                                    <tr className="os-doc-json-row">
                                                        <td colSpan={cols.length + 2}>
                                                            <pre className="os-doc-json">{JSON.stringify(d.source, null, 2)}</pre>
                                                        </td>
                                                    </tr>
                                                )}
                                            </React.Fragment>
                                        ))}
                                    </tbody>
                                </table>
                            </div>

                            {pages > 1 && (
                                <div className="os-pagination">
                                    <button className="acp-btn acp-btn-secondary acp-btn-sm" disabled={page === 0} onClick={() => setFrom(0)}>«</button>
                                    <button className="acp-btn acp-btn-secondary acp-btn-sm" disabled={page === 0} onClick={() => setFrom(Math.max(0, from - PAGE_SIZE))}>‹</button>
                                    <span className="os-page-info">Page {page + 1} of {pages}</span>
                                    <button className="acp-btn acp-btn-secondary acp-btn-sm" disabled={page >= pages - 1} onClick={() => setFrom(from + PAGE_SIZE)}>›</button>
                                    <button className="acp-btn acp-btn-secondary acp-btn-sm" disabled={page >= pages - 1} onClick={() => setFrom((pages - 1) * PAGE_SIZE)}>»</button>
                                </div>
                            )}
                        </>
                    )}
                </div>
            )}
            </>)}

            {activeTab === 'history' && (
                <div className="acp-card">
                    {histError && <div className="os-msg os-msg-error">{histError}</div>}
                    {!indexName ? (
                        <div className="acp-empty">Select a partner and chain to see its upsert history.</div>
                    ) : histDocs.length === 0 && !histLoading ? (
                        <div className="acp-empty">No upsert history yet for this index.</div>
                    ) : (
                        <>
                            <div className="os-doc-table-wrap">
                                <table className="acp-table os-doc-table">
                                    <thead><tr><th>When</th><th>File</th><th>Inserted</th><th>Updated</th><th>Total</th></tr></thead>
                                    <tbody>
                                        {histDocs.map((d) => (
                                            <tr key={d.id}>
                                                <td>{d.source?.created_at ? new Date(d.source.created_at).toLocaleString() : ''}</td>
                                                <td style={{ fontFamily: 'monospace', fontSize: '0.82rem', wordBreak: 'break-all' }}>{d.source?.file}</td>
                                                <td>{fmt(d.source?.inserted)}</td>
                                                <td>{fmt(d.source?.updated)}</td>
                                                <td>{fmt(d.source?.total ?? d.source?.count)}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                            {histTotal > PAGE_SIZE && (
                                <div className="os-pagination">
                                    <button className="acp-btn acp-btn-secondary acp-btn-sm" disabled={histFrom === 0} onClick={() => setHistFrom(Math.max(0, histFrom - PAGE_SIZE))}>‹ Newer</button>
                                    <span className="os-page-info">{histFrom + 1}–{Math.min(histFrom + PAGE_SIZE, histTotal)} of {histTotal}</span>
                                    <button className="acp-btn acp-btn-secondary acp-btn-sm" disabled={histFrom + PAGE_SIZE >= histTotal} onClick={() => setHistFrom(histFrom + PAGE_SIZE)}>Older ›</button>
                                </div>
                            )}
                        </>
                    )}
                </div>
            )}

            {pickerOpen && (
                <S3FilePickerModal
                    filterDecryptedOnly
                    defaultBucket={parseS3Loc(decryptLocation).bucket}
                    defaultPrefix="decrypted/"
                    fileStatus={(f) => (uploadedKeys.has(f.key) ? 'uploaded' : 'pending')}
                    onSelect={(f) => { setSelectedFile(f); setPickerOpen(false); }}
                    onClose={() => setPickerOpen(false)}
                />
            )}

            {decryptPickerOpen && (() => {
                const { bucket: dBucket, prefix: dPrefix } = parseS3Loc(decryptLocation);
                return (
                    <S3FilePickerModal
                        multiSelect
                        defaultBucket={dBucket}
                        defaultPrefix={dPrefix}
                        onSelect={(files) => { setDecryptFiles(Array.isArray(files) ? files : [files]); setDecryptPickerOpen(false); }}
                        onClose={() => setDecryptPickerOpen(false)}
                    />
                );
            })()}
        </div>
    );
}
